<template>
  <div class="bg-white">
    <div class="d-flex align-items-center gap-2">
      <el-select v-model="type_chart" style="width: 160px;">
        <el-option
          v-for="opt in chartTypeOptions"
          :key="opt.value"
          :value="opt.value"
          :label="opt.label"
        >
          <div class="chart-option">
            <i :class="opt.icon" class="chart-option-icon"></i>
            <div>
              <div class="chart-option-name">{{ opt.label }}</div>
              <div class="chart-option-desc">{{ opt.desc }}</div>
            </div>
          </div>
        </el-option>
      </el-select>

      <el-popover
        v-model:visible="settingsOpen"
        placement="bottom-start"
        :width="300"
        trigger="click"
        popper-class="chart-settings-popover"
      >
        <template #reference>
          <el-button circle :type="hasCustomVisual ? 'primary' : 'default'" plain>
            <i class="fas fa-sliders-h"></i>
          </el-button>
        </template>

        <div class="cs-body">
          <div class="cs-title">{{ $t('ReportBuilder.visual.title') }}</div>

          <div class="cs-row">
            <span class="cs-label">{{ $t('ReportBuilder.visual.hideLegend') }}</span>
            <el-switch :model-value="visualHideLegend" @change="setVisual('hideLegend', $event)" />
          </div>

          <div class="cs-row" v-if="supportsDateLabels">
            <span class="cs-label">{{ $t('ReportBuilder.visual.hideDateLabels') }}</span>
            <el-switch :model-value="visualHideDateLabels" @change="setVisual('hideDateLabels', $event)" />
          </div>

          <div class="cs-row" v-if="isPie">
            <span class="cs-label">{{ $t('ReportBuilder.visual.showValues') }}</span>
            <el-switch :model-value="visualShowValues" @change="setVisual('showValues', $event)" />
          </div>

          <div class="cs-row cs-row--col" v-if="supportsAxisScale">
            <span class="cs-label">{{ $t('ReportBuilder.visual.yAxisScale') }}</span>
            <el-select :model-value="visualYAxisScale" @change="setVisual('yAxisScale', $event)" style="width: 100%;">
              <el-option value="zero" :label="$t('ReportBuilder.visual.yAxisScaleZero')" />
              <el-option value="auto" :label="$t('ReportBuilder.visual.yAxisScaleAuto')" />
            </el-select>
            <span class="cs-hint">{{ $t('ReportBuilder.visual.yAxisScaleHint') }}</span>
          </div>

          <div class="cs-row cs-row--col" v-if="supportsAxisScale">
            <span class="cs-label">{{ $t('ReportBuilder.visual.gridStyle') }}</span>
            <el-select :model-value="visualGridStyle" @change="setVisual('gridStyle', $event)" style="width: 100%;">
              <el-option value="full" :label="$t('ReportBuilder.visual.gridFull')" />
              <el-option value="horizontal" :label="$t('ReportBuilder.visual.gridHorizontal')" />
              <el-option value="vertical" :label="$t('ReportBuilder.visual.gridVertical')" />
              <el-option value="none" :label="$t('ReportBuilder.visual.gridNone')" />
            </el-select>
          </div>

          <!-- Толщина/пунктир/сглаживание/точки/заливка сразу для ВСЕХ серий отчёта —
               в отличие от per-row пикера в детальной таблице, не требует, чтобы отчёт
               уже отработал и строки (например, страны разбивки) были уже известны. -->
          <div class="cs-row cs-row--col" v-if="supportsSeriesStyle">
            <SeriesColorPicker
              :line-width="globalSeriesStyle.lineWidth"
              :line-style="globalSeriesStyle.lineStyle"
              :line-tension="globalSeriesStyle.lineTension"
              :show-points="globalSeriesStyle.showPoints"
              :fill="globalSeriesStyle.fill"
              :show-color="false"
              :palette="[]"
              :line-widths="lineWidths"
              @update="applyGlobalSeriesStyle"
            >
              <template #reference>
                <el-button size="small" plain style="width: 100%;">
                  <i class="fas fa-grip-lines me-1"></i>
                  {{ $t('ReportBuilder.visual.seriesStyleAll') }}
                </el-button>
              </template>
            </SeriesColorPicker>
            <span class="cs-hint">{{ $t('ReportBuilder.visual.seriesStyleAllHint') }}</span>
          </div>
        </div>
      </el-popover>
    </div>
  </div>
</template>

<script>
import { effectiveChartType, isUtmTrendsBreakdown } from "@/helpers/reports/utmTrends"
import SeriesColorPicker from "@/views/report_builder/builder/_series_color_picker.vue"

export default {
  name: "_control_chart",
  components: { SeriesColorPicker },
  inject: {
    injectedReportData: {
      from: 'injectedReportData',
      default: null
    }
  },
  data() {
    return {
      settingsOpen: false,
      lineWidths: [
        { value: 1 },
        { value: 2 },
        { value: 3 },
        { value: 4 },
      ]
    }
  },
  computed: {
    reportData() {
      return this.injectedReportData || this.$store.state.ReportFromBuilder
    },
    chartTypeOptions() {
      // В SQL-режиме форма данных задаётся запросом: карта требует своей разбивки
      // по странам, зато нужен «сырой» табличный вид результата.
      if (this.reportData?.type === 'sql') {
        return [
          {
            value: 'table',
            label: this.$t('ReportBuilder.chartTypes.table'),
            desc: this.$t('ReportBuilder.chartTypes.tableDesc'),
            icon: 'fas fa-table'
          },
          ...this.baseChartTypeOptions.filter(opt => opt.value !== 'map')
        ]
      }
      return this.baseChartTypeOptions
    },
    baseChartTypeOptions() {
      return [
        {
          value: 'line',
          label: this.$t('ReportBuilder.chartTypes.line'),
          desc: this.$t('ReportBuilder.chartTypes.lineDesc'),
          icon: 'fas fa-chart-line'
        },
        {
          value: 'area',
          label: this.$t('ReportBuilder.chartTypes.area'),
          desc: this.$t('ReportBuilder.chartTypes.areaDesc'),
          icon: 'fas fa-chart-area'
        },
        {
          value: 'bar',
          label: this.$t('ReportBuilder.chartTypes.bar'),
          desc: this.$t('ReportBuilder.chartTypes.barDesc'),
          // fa-chart-bar renders as HORIZONTAL bars in this icon set — fa-chart-column is
          // the vertical one, which is what this (and bar_stacked, below) actually render.
          icon: 'fas fa-chart-column'
        },
        {
          value: 'bar_stacked',
          label: this.$t('ReportBuilder.chartTypes.barStacked'),
          desc: this.$t('ReportBuilder.chartTypes.barStackedDesc'),
          icon: 'fas fa-chart-column'
        },
        {
          value: 'line_cumulative',
          label: this.$t('ReportBuilder.chartTypes.lineCumulative'),
          desc: this.$t('ReportBuilder.chartTypes.lineCumulativeDesc'),
          icon: 'fas fa-chart-line'
        },
        {
          value: 'pie',
          label: this.$t('ReportBuilder.chartTypes.pie'),
          desc: this.$t('ReportBuilder.chartTypes.pieDesc'),
          icon: 'fas fa-chart-pie'
        },
        {
          value: 'bar_horizontal',
          label: this.$t('ReportBuilder.chartTypes.barHorizontal'),
          desc: this.$t('ReportBuilder.chartTypes.barHorizontalDesc'),
          // fa-chart-bar is the one that actually renders horizontal bars in this icon set.
          icon: 'fas fa-chart-bar'
        },
        {
          value: 'number',
          label: this.$t('ReportBuilder.chartTypes.number'),
          desc: this.$t('ReportBuilder.chartTypes.numberDesc'),
          icon: 'fas fa-hashtag'
        },
        {
          value: 'map',
          label: this.$t('ReportBuilder.chartTypes.map'),
          desc: this.$t('ReportBuilder.chartTypes.mapDesc'),
          icon: 'fas fa-globe'
        },
      ]
    },
    type_chart: {
      get() {
        // Normalizes the legacy literal 'utm_trends' value (reports saved before line/bar
        // was decoupled from the UTM-breakdown query engine) to a real, selectable value.
        return effectiveChartType(this.reportData)
      },
      set(val) {
        if (this.injectedReportData) {
          this.injectedReportData.type_chart = val;
          if (this.injectedReportData.source_request) {
            this.injectedReportData.source_request.type_chart = val;
          }
        }
        this.$store.commit('ReportFromBuilder/setTypeChart', val)
        if (val === 'map') {
          this.ensureGeoSeparator()
        }
      }
    },
    visualHideLegend() {
      return !!this.reportData?.visual?.hideLegend
    },
    visualHideDateLabels() {
      return !!this.reportData?.visual?.hideDateLabels
    },
    visualGridStyle() {
      return this.reportData?.visual?.gridStyle || 'full'
    },
    visualYAxisScale() {
      return this.reportData?.visual?.yAxisScale || 'zero'
    },
    visualShowValues() {
      return !!this.reportData?.visual?.showValues
    },
    isPie() {
      return effectiveChartType(this.reportData) === 'pie'
    },
    // Только виды с числовой осью — у круговой диаграммы, KPI и карты её нет
    supportsAxisScale() {
      const t = effectiveChartType(this.reportData)
      return ['line', 'area', 'line_cumulative', 'bar', 'bar_stacked', 'bar_horizontal'].includes(t)
    },
    // Подписи дат — только у видов с временной осью X; у горизонтального бара ось X
    // числовая (не даты), у круговой/KPI/карты осей нет вовсе
    supportsDateLabels() {
      const t = effectiveChartType(this.reportData)
      return ['line', 'area', 'line_cumulative', 'bar', 'bar_stacked'].includes(t)
    },
    hasCustomVisual() {
      const v = this.reportData?.visual || {}
      return !!v.hideLegend || !!v.hideDateLabels || !!v.showValues
        || (v.gridStyle && v.gridStyle !== 'full')
        || (v.yAxisScale && v.yAxisScale !== 'zero')
    },
    // Line-style controls only mean something for multi-request line/bar-ish reports —
    // UTM-breakdown series aren't backed by `requests` at all (see buildUtmTrendsData),
    // so there's nothing here for this control to write to.
    supportsSeriesStyle() {
      if (isUtmTrendsBreakdown(this.reportData)) return false
      const t = effectiveChartType(this.reportData)
      return ['line', 'area', 'line_cumulative', 'bar', 'bar_stacked'].includes(t)
    },
    // Represents the whole report's line style by its first request — good enough as a
    // "what's currently set" readout since applyGlobalSeriesStyle keeps every request in sync.
    globalSeriesStyle() {
      const r = (this.reportData?.source_request?.requests || [])[0] || {}
      return {
        lineWidth: r.lineWidth ?? 1,
        lineStyle: r.lineStyle ?? 'solid',
        lineTension: r.lineTension ?? 'smooth',
        showPoints: r.showPoints ?? false,
        fill: r.fill ?? (effectiveChartType(this.reportData) === 'area')
      }
    }
  },
  methods: {
    // Patches EVERY request with the same style fields — the one place a user can set
    // line width/dash/tension/points/fill for a report before (or regardless of) which
    // series actually come back, unlike the per-row picker in the detail table below.
    applyGlobalSeriesStyle(patch) {
      const requests = this.reportData?.source_request?.requests || []
      if (!requests.length) return
      const updated = requests.map(r => ({ ...r, ...patch }))
      if (this.injectedReportData) {
        this.injectedReportData.source_request.requests = updated
        return
      }
      this.$store.commit('ReportFromBuilder/setRequests', updated)
    },
    // The map aggregates by country, so it needs a countryCode breakdown.
    // Replace current separators with it and widen the limit to fit all countries.
    ensureGeoSeparator() {
      const GEO_COUNTRY_FIELD = 'events.properties.user.geo.countryCode'
      const separators = this.reportData?.source_request?.separators || []
      const hasGeo = separators.some(s => (typeof s === 'string' ? s : s?.property) === GEO_COUNTRY_FIELD)
      if (hasGeo) return
      if (this.injectedReportData?.source_request) {
        this.injectedReportData.source_request.separators = [GEO_COUNTRY_FIELD]
        this.injectedReportData.source_request.breakdown_limit = 300
      }
      this.$store.commit('ReportFromBuilder/setSeparators', [GEO_COUNTRY_FIELD])
      this.$store.commit('ReportFromBuilder/setBreakdownLimit', 300)
    },
    setVisual(key, value) {
      if (this.injectedReportData) {
        this.injectedReportData.visual = { ...(this.injectedReportData.visual || {}), [key]: value }
      }
      this.$store.commit('ReportFromBuilder/setVisualOption', { key, value })
    }
  }
}
</script>

<style scoped>
.chart-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 0;
}

.chart-option-icon {
  color: #909399;
  width: 14px;
  flex-shrink: 0;
}

.chart-option-name {
  font-size: 13px;
  color: #303133;
  line-height: 1.3;
}

.chart-option-desc {
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
}

.cs-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 2px;
}

.cs-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--ui-text-1, #303133);
  padding-bottom: 6px;
  border-bottom: 1px solid var(--ui-border, #ebeef5);
}

.cs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cs-row--col {
  flex-direction: column;
  align-items: flex-start;
}

.cs-label {
  font-size: 13px;
  color: var(--ui-text-2, #606266);
}

.cs-hint {
  font-size: 11px;
  line-height: 1.35;
  color: var(--ui-text-3, #909399);
}
</style>
